/**
 * Render OC FAB Chat
 * External API for embedding the OC FAB Widget via script tags
 */

import { createRoot, Root } from 'react-dom/client';

import { OcChatConfig, OcFabWidgetOptions } from '../../types/oc';
import { OcFabWidget } from './OcFabWidget';
import { ensureInterStylesheet } from './utils/ensureInterStylesheet';

// Store root reference for unmounting
let ocFabRoot: Root | null = null;

/**
 * Render OC FAB Chat widget into a DOM element
 * @param elementOrId - HTML element or element ID to render into
 * @param config - Required OC chat configuration
 * @param options - Optional widget customization options
 */
export const renderOcFabChat = async (
  elementOrId: HTMLElement | string,
  config: OcChatConfig,
  options?: OcFabWidgetOptions,
): Promise<void> => {
  ensureInterStylesheet();

  // Get the target element
  const element =
    typeof elementOrId === 'string'
      ? document.getElementById(elementOrId)
      : elementOrId;

  if (!element) {
    throw new Error(
      `Element not found: ${typeof elementOrId === 'string' ? elementOrId : 'provided element'}`,
    );
  }

  // Clean up any existing root
  if (ocFabRoot) {
    try {
      ocFabRoot.unmount();
    } catch (e) {
      // Ignore errors on unmount
    }
  }

  // Create new root and render
  ocFabRoot = createRoot(element);
  ocFabRoot.render(<OcFabWidget config={config} options={options} />);
};



/**
 * Unmount OC FAB Chat widget
 * @param removeElement - Whether to remove the DOM element after unmounting
 */
export const unmountOcFabChat = (removeElement = false): void => {
  if (ocFabRoot) {
    try {
      ocFabRoot.unmount();
      ocFabRoot = null;

      // Optionally remove the element from DOM
      if (removeElement) {
        // Note: We don't have a reference to the element here
        // This would require storing the element reference
        // eslint-disable-next-line no-console
        console.warn('removeElement option not fully implemented in unmountOcFabChat');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error unmounting OC FAB Chat:', e);
    }
  }
};

